import React, { useEffect, useMemo, useState } from "react";

import { getApiUrl } from "@/config";
import {
  BrandSettings,
  DEFAULT_BRAND_SETTINGS,
  getBrand,
} from "@/service/Brands";
import EventSystem from "@/service/EventSystem";
import { initializedResponse } from "@/service/IFrame";
import InteractionService, { InteractionApi } from "@/service/Interaction";
import LocalStorageService from "@/service/LocalStorage";
import {
  ApplicationPlatform,
  HansIdResponse,
  Interaction,
  InteractionResult,
  Participant,
  RegistrationStatus,
} from "@/service/Model";
import { ThemeName } from "@/styles/Theme";
import { getBrandIcons } from "@/styles/utils/Icons";
import { log } from "@/utils/logger";

export type IGlobalResponse = {
  interaction: Interaction;
  interactionResults: InteractionResult;
};

export type LocalSettings = {
  questionOutroOptIn?: boolean;
};

export type GlobalContextProps = {
  brandSettings: BrandSettings;
  icons: IIcons;
  query: IQuery;
  preview: boolean;
  hyperAvailable: boolean;
  isNewsappStandalone: boolean;
  registrationStatus: RegistrationStatus;
  response?: IGlobalResponse;
  error?: Error;
  tealiumConfig?: ITealium;
  hansId?: string;
  handleRegistration: (
    payload: Record<string, string | boolean>
  ) => Promise<any>;
  setQuestionOutroOptIn: (optIn: boolean) => void;
  questionOutroOptIn: boolean;
};

type GlobalProviderProps = {
  query?: IQuery;
  mockResponse?: IGlobalResponse;
  children?: React.ReactNode;
};

const GlobalContext = React.createContext<GlobalContextProps>({
  questionOutroOptIn: false,
  preview: false,
  hyperAvailable: false,
  isNewsappStandalone: false,
  registrationStatus: RegistrationStatus.None,
  query: {},
  brandSettings: DEFAULT_BRAND_SETTINGS,
  handleRegistration: () => new Promise<any>(() => null),
  setQuestionOutroOptIn: () => null,
  icons: {
    Checkmark: "",
    Clock: "",
    Close: "",
    CloseLite: "",
    Facebook: "",
    Fail: "",
    PageLeft: "",
    PageRight: "",
    Picker: "",
    Slider: "",
    Success: "",
    ThumbsDown: "",
    ThumbsUp: "",
    X: "",
    Warning: "",
    WhatsApp: "",
    BubbleCheckmark: "",
  },
});

export const GlobalProvider: React.FC<GlobalProviderProps> = ({
  query = {},
  mockResponse,
  children,
}): React.JSX.Element => {
  const isNewsappStandalone = query.platform === ApplicationPlatform.Newsapp;
  const preview = query.preview === true;
  const locale = query.locale?.toLowerCase();
  const themeName = query.themeName?.toLowerCase() as ThemeName | undefined;

  const [hyperAvailable, setHyperAvailable] = useState<boolean>(false);
  const [accessibilityModeEnabled, setAccessibilityModeEnabled] =
    useState<boolean>(false);
  const [tealiumConfig, setTealiumConfig] = useState<ITealium | undefined>(
    undefined
  );
  const [hansId, setHansId] = useState<string | undefined>(undefined);
  const [registrationStatus, setRegistrationStatus] =
    useState<RegistrationStatus>(RegistrationStatus.None);
  const [error, setError] = useState<Error | undefined>(undefined);

  const [response, setResponse] = useState<IGlobalResponse | undefined>(
    mockResponse
  );

  const initialBrandSettings = mockResponse
    ? getBrand(
        mockResponse.interaction.organizationId,
        mockResponse.interaction.category,
        locale,
        themeName
      )
    : DEFAULT_BRAND_SETTINGS;

  const [brandSettings, setBrandSettings] =
    useState<BrandSettings>(initialBrandSettings);
  const [icons, setIcons] = useState<IIcons>(
    getBrandIcons(brandSettings.themeName)
  );

  const [questionOutroOptIn, setQuestionOutroOptIn] = useState<boolean>(false);

  useEffect(() => {
    // Returns immediately if the response was initialized with a mocked response (jest tests) or interaction id is missing
    if (response) {
      return;
    }

    if (query.id) {
      initEventListener();
      init(query.id, preview);
    }
  }, []);

  useEffect(() => {
    if (response) {
      // Sync states with local storage
      LocalStorageService.setLocalSettings(response.interaction.id, {
        questionOutroOptIn,
      });
    }
  }, [questionOutroOptIn]);

  useEffect(() => {
    if (brandSettings.themeName === ThemeName.welt) {
      setBrandSettings({
        ...brandSettings,
        themeName: ThemeName.welt_contrast,
      });
    }
  }, [accessibilityModeEnabled]);

  useEffect(() => {
    if (response) {
      const { organizationId, category } = response.interaction;
      const updatedBrandSettings = getBrand(
        organizationId,
        category,
        locale,
        themeName
      );
      setBrandSettings(updatedBrandSettings);
      setIcons(getBrandIcons(updatedBrandSettings.themeName));
    }
  }, [themeName, locale]);

  const initEventListener = (): void => {
    EventSystem.subscribe("hyper:ping", (data: initializedResponse) => {
      setHyperAvailable(true);
      if (data.accessibilityModeEnabled) {
        setAccessibilityModeEnabled(true);
      }
    });

    // Listener for tealium_id via LA-Hyper
    EventSystem.subscribe(
      "tealium:received",
      (tealiumResponse: ITealium | undefined): void => {
        setTealiumConfig(tealiumResponse);
      }
    );

    EventSystem.subscribe("hansId:received", (data: HansIdResponse) => {
      // hansId = 'rejected': The user has not consented to the use of tracking IDs.
      if (data.consent) {
        // Always cast hansId as string (backwards compatible with UUIDs)
        setHansId(String(data.hansId));
      }
    });
  };
  const init = (interactionId: string, preview: boolean): void => {
    api
      .getInteractionAndResults(interactionId, preview)
      .then(([interactionResponse, resultResponse]) => {
        const interaction = interactionResponse.data!;
        const interactionResults = resultResponse.data!;
        setupTheme(interaction.organizationId, interaction.category);
        setResponse({
          interaction,
          interactionResults,
        });
        initLocalSettings(interaction.id);
      })
      .catch((error: Error) => setError(error));
  };

  const setupTheme = (organizationId: string, category: string) => {
    const brandSettings = getBrand(organizationId, category, locale, themeName);
    setBrandSettings(brandSettings);
    setIcons(getBrandIcons(brandSettings.themeName));
  };

  const initLocalSettings = (interactionId: string) => {
    const localSettings = LocalStorageService.getLocalSettings(interactionId);
    setQuestionOutroOptIn(!!localSettings?.questionOutroOptIn);
  };

  const handleRegistration = async (
    payload: Record<string, string | boolean>
  ): Promise<RegistrationStatus | undefined> => {
    if (!response || !payload) {
      return;
    }

    const { interaction } = response;
    const participant = payload as Participant;

    if (hansId) {
      participant.hansId = hansId;
    }

    if (interaction.outro?.campaignId) {
      participant.campaignId = interaction.outro.campaignId;
    }

    setRegistrationStatus(RegistrationStatus.Pending);

    try {
      const response = await api.postParticipation({
        ...participant,
        interactionId: interaction.id,
        interactionType: interaction.type,
      });
      if (response.ok) {
        setRegistrationStatus(RegistrationStatus.Success);
        return RegistrationStatus.Success;
      } else {
        setRegistrationStatus(RegistrationStatus.Error);
        log.error(
          `Error while submit registration form: ${response.status}: ${response.text}`
        );
      }
    } catch (e) {
      log.error("Error while submit registration form:" + e);
    }
  };

  const api = useMemo<InteractionApi>(
    () => new InteractionService(getApiUrl(preview)),
    [preview]
  );

  const value = useMemo<GlobalContextProps>(
    () => ({
      handleRegistration,
      setQuestionOutroOptIn,
      questionOutroOptIn,
      hyperAvailable,
      accessibilityModeEnabled,
      isNewsappStandalone,
      preview,
      brandSettings,
      icons,
      query,
      registrationStatus,
      response,
      error,
      tealiumConfig,
      hansId,
    }),
    [
      hyperAvailable,
      accessibilityModeEnabled,
      isNewsappStandalone,
      preview,
      brandSettings,
      icons,
      query,
      registrationStatus,
      response,
      error,
      tealiumConfig,
      hansId,
      questionOutroOptIn,
    ]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

export const GlobalConsumer = GlobalContext.Consumer;
export default GlobalContext;
